import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { IHandleLinkQuery } from "@gloria-public/register/domain/interfaces/handle-link.interface";
import { HandleLinkService } from "@gloria-public/register/domain/services/handle-link.service";
import { ExpiredValidationComponent } from "@gloria-public/register/views/link-page/components/expired-validation/expired-validation.component";
import { TranslateService } from "@ngx-translate/core";
import { ApiService } from "@shared/services/api.service";
import { ConnectionEventsService } from "@shared/services/connection-events.service";
import { SessionService } from "@shared/services/session.service";
import { Subject, forkJoin, timer } from "rxjs";
import { first, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-link-page",
  templateUrl: "./link-page.component.html",
  styleUrls: ["./link-page.component.scss"],
})
export class LinkPageComponent implements OnInit, OnDestroy {
  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private handleLinkService: HandleLinkService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private connectionEventsService: ConnectionEventsService,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    // Disconnect any user that was connected
    this.sessionService.clear();
    this.apiService.suspendVerifyTokensFlow();
    this.apiService.suspendConsentFlow();

    this.route.queryParams.pipe(takeUntil(this.onDestroy$)).subscribe((params) => {
      if (Object.keys(params).length === 0) {
        this.router.navigate(["/not-found"]);
        this.apiService.resumeVerifyTokensFlow();
        this.apiService.resumeConsentFlow();
        return;
      }

      const linkParams: IHandleLinkQuery = {
        token: params["token"],
        origin: params["origin"],
      };

      this.processLink(linkParams);
    });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Processes the incoming validation link and handles authentication and redirection
   */
  private processLink(linkParams: IHandleLinkQuery): void {
    forkJoin({
      response: this.handleLinkService.handleLink(linkParams).pipe(first()),
      delay: timer(3000), // Display loader at least 3 seconds before redirecting to avoid weird jumps
    }).subscribe({
      next: ({ response }) => {
        this.translateService.use(response.language);
        this.connectionEventsService.notifyLogin(response.tokens, response.language);
        this.apiService.resumeVerifyTokensFlow();
        this.apiService.resumeConsentFlow();
        this.router.navigate([response.redirection]);
      },
      error: (error) => {
        this.apiService.resumeVerifyTokensFlow();
        this.apiService.resumeConsentFlow();
        if (error.status === 401) {
          this.dialog.open(ExpiredValidationComponent, {
            maxWidth: "100vw",
            maxHeight: "100vh",
          });
        } else {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
          this.router.navigate(["/not-found"]);
        }
      },
    });
  }
}
